import React, {
  useCallback, useContext, useEffect, useMemo, memo
} from 'react';
import FormContext from './FormContext';

/**
 * @typedef {Object} InputProps
 * @property {*} value - The controlled value for the field managed by the FormContext
 * @property {string[]|null} errors - Any validation errors for the field managed by the FormContext
 * @property {function} onChange - An onChange handler that will connect the field to the
 *   FormContext - will pass arguments through to `props.onChange` without modification, but expects
 *   the arguments to be either `(name, value)` or `(event)`
 * @property {function} onBlur - An onBlur handler that will trigger on-blur validation if enabled
 *   for the field - will pass arguments through to `props.onBlur` without modification and does not
 *   otherwise use the arguments provided so no special format is required
 */

/**
 * Handles connecting component state to the context
 * @param {Object} props - The props for the component
 * @param {string} props.name - The field in the data where the value should be held
 * @param {*} [props.defaultValue] - The value the field should have when it is first rendered
 * @param {Object|Object[]} [props.validators] - Validators for the field
 * @param {function} [props.onChange] - An onChange handler for the field
 * @param {function} [props.onBlur] - An onBlur handler for the field
 * @param {boolean} [props.validateOnBlur = true] - A flag to control triggering validation on blur
 * @param [overrideFormValue]
 * @returns {InputProps}
 */
export const useInputAPI = (props, overrideFormValue) => {
  const formContextValue = useContext(FormContext);
  const formValue = overrideFormValue || formContextValue;
  const value = formValue?.data?.[props.name];
  const errors = formValue?.errors?.[props.name];
  const { registerValidators, unregisterValidators, onChange: contextOnChange } = formValue || {};
  const {
    onChange: onChangeProp,
    onBlur: onBlurProp,
    name: nameProp,
    validators,
    validateOnBlur
  } = props;

  useEffect(() => {
    if (contextOnChange && props.defaultValue) {
      contextOnChange(nameProp, props.defaultValue);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (registerValidators && validators) {
      registerValidators(nameProp, validators);
    }
  }, [registerValidators, nameProp, validators]);

  useEffect(() => {
    if (unregisterValidators) {
      return () => unregisterValidators(nameProp);
    }
    return undefined;
  }, [unregisterValidators, nameProp]);

  const onChange = useCallback((...args) => {
    // some ugly duck typing to support either (name, value) or (event) as args
    const standardArgs = typeof args[0] === 'string' && args.length > 1;
    const name = standardArgs ? args[0] : args[0].target.name;
    const newValue = standardArgs ? args[1] : args[0].target.value;

    if (onChangeProp) {
      onChangeProp(name, newValue, args[2]);
    }

    contextOnChange(name, newValue, args[2]);
  }, [contextOnChange, onChangeProp]);

  const onBlur = useCallback((...args) => {
    if (onBlurProp) {
      onBlurProp(nameProp, value, args[2]);
    }

    if (validateOnBlur === undefined || validateOnBlur) {
      contextOnChange(nameProp, value, true);
    }
  }, [contextOnChange, nameProp, onBlurProp, validateOnBlur, value]);

  const inputProps = useMemo(() => ({
    errors,
    value,
    onChange,
    onBlur
  }), [errors, onBlur, onChange, value]);

  return formValue && !props.disableInputAPI ? inputProps : null;
};

/**
 * Applies the result of useInputAPI to the provided component and memoizes it if needed
 *
 * @param Component {function}
 * @returns {function} Wrapped Component
 */
export const withInputAPI = Component => {
  const Memoized = (
    Component.$$typeof?.toString()?.includes('react.memo')
    || Component.prototype?.isReactComponent
  ) ? Component : memo(Component);

  const Wrapped = (props) => {
    const { disableInputAPI, ...rest } = props; // eslint-disable-line react/prop-types
    const inputProps = useInputAPI(props);

    return <Memoized {...rest} {...inputProps} />;
  };
  /* istanbul ignore next */
  Wrapped.displayName = `InputAPI(${Component.displayName || Component.name || 'Component'})`;

  return Wrapped;
};
